import React, { useCallback, useEffect, useRef, useState } from 'react';
import FilterBadge from '../filterBadge/FilterBadge';
import styles from './FilterBadgesOverflow.module.scss';

export type FilterBadgeItem = {
    key: string;
    label: string;
    value: string | React.ReactElement;
    onRemove?: () => void;
};

type Props = {
    items: FilterBadgeItem[];
    className?: string;
    align?: 'left' | 'right';
};

const FilterBadgesOverflow: React.FC<Props> = ({ items, className, align = 'right' }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(items.length);
    const [isMeasuring, setIsMeasuring] = useState(true);

    const calculateVisibleBadges = useCallback(() => {
        const container = containerRef.current;
        if (!container || items.length === 0) {
            setVisibleCount(items.length);
            setIsMeasuring(false);
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;

        if (containerWidth === 0) {
            requestAnimationFrame(() => calculateVisibleBadges());
            return;
        }

        const badges = Array.from(container.querySelectorAll('[data-badge]'));
        const moreIndicatorWidth = 45;
        const gap = 6;

        let totalWidth = 0;
        let count = 0;

        for (let i = 0; i < badges.length; i++) {
            const badge = badges[i] as HTMLElement;
            const badgeWidth = badge.offsetWidth;

            if (badgeWidth === 0 && i === 0) {
                requestAnimationFrame(() => calculateVisibleBadges());
                return;
            }

            const isLastBadge = i === badges.length - 1;
            const spaceNeeded = totalWidth + badgeWidth + (isLastBadge ? 0 : moreIndicatorWidth);

            if (spaceNeeded <= containerWidth) {
                totalWidth += badgeWidth + gap;
                count++;
            } else {
                break;
            }
        }

        setVisibleCount(count);
        setIsMeasuring(false);
    }, [items.length]);

    useEffect(() => {
        setIsMeasuring(true);
        setVisibleCount(items.length);
    }, [items.length]);

    useEffect(() => {
        if (isMeasuring) {
            let rafId2: number;
            const rafId1 = requestAnimationFrame(() => {
                rafId2 = requestAnimationFrame(() => {
                    calculateVisibleBadges();
                });
            });
            return () => {
                cancelAnimationFrame(rafId1);
                if (rafId2) cancelAnimationFrame(rafId2);
            };
        }
    }, [isMeasuring, calculateVisibleBadges]);

    useEffect(() => {
        const handleResize = () => {
            setIsMeasuring(true);
            setVisibleCount(items.length);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [items.length]);

    const hiddenBadgesCount = items.length - visibleCount;

    if (items.length === 0) {
        return null;
    }

    return (
        <div
            className={`${styles.container} ${className || ''}`}
            ref={containerRef}
            style={isMeasuring ? { visibility: 'hidden' } : undefined}
        >
            {items.map((item, index) => (
                <FilterBadge
                    key={item.key}
                    data-badge
                    title={item.label}
                    value={item.value as string}
                    onRemoveFilter={item.onRemove}
                    className={styles.badge}
                    style={!isMeasuring && index >= visibleCount ? { display: 'none' } : undefined}
                />
            ))}
            {!isMeasuring && hiddenBadgesCount > 0 && (
                <div className={styles.moreBadgeContainer}>
                    <span className={styles.moreBadge}>+{hiddenBadgesCount}</span>
                    <div className={`${styles.hiddenBadgesDropdown} ${align === 'left' ? styles.dropdownAlignLeft : ''}`}>
                        {items.slice(visibleCount).map(item => (
                            <FilterBadge
                                key={item.key}
                                title={item.label}
                                value={item.value as string}
                                onRemoveFilter={item.onRemove}
                                className={styles.dropdownBadge}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterBadgesOverflow;
